import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, makeStyles, mergeClasses, Switch, Text } from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { countBy, uniq } from 'es-toolkit/array';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { useCommonStyles } from '../../common/hooks';
import { getUserNames } from '../../common/string/get-user-names';
import { getTooltipContentByFieldName } from '../../common/tooltip/tooltip-get-content';
import { AwardJudge } from '../../models';
import { emailAddressSchema } from '../../models/schemas/email-address.schema';
import { parseEmailAddresses } from '../../shared/functions';
import { ValidationMessageList } from './ValidationMessageList';
import { FlexBox } from './flex-box';
import { FormInput } from './form/FormInput';
import './JudgeAssignmentModal.scss';

interface JudgeAssignmentModalProps {
  isModalOpen: boolean;
  onAddJudges(emails: string[]): void;
  onDeleteJudge(judge: AwardJudge): void;
  onUpdateJudgeStatus(judge: AwardJudge, status: boolean): void;
  onDismissModal(): void;
  onSubmitModal(): void;
  addedJudges: AwardJudge[];
  judges: AwardJudge[];
  activeJudgesCount: number;
  inactiveJudgesCount: number;
}

const useStyles = makeStyles({
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '12px',
  }
});

const JudgeAssignmentModalImpl: React.FC<JudgeAssignmentModalProps> = (props) => {
  const {
    isModalOpen,
    onAddJudges,
    onDeleteJudge,
    onUpdateJudgeStatus,
    onDismissModal,
    onSubmitModal,
    judges,
    addedJudges,
    activeJudgesCount,
    inactiveJudgesCount,
  } = props;

  const commonStyles = useCommonStyles();
  const styles = useStyles();
  const [emailsToAdd, setEmailsToAdd] = useState<string>();
  const [isBeingValidated, setIsBeingValidated] = useState<boolean>(false);

  const emailAddressesToAdd = parseEmailAddresses(emailsToAdd || '');

  const emailAddressesToValidate = emailAddressesToAdd.length ? emailAddressesToAdd : [''];

  const emailAddressCounts = countBy(
    emailAddressesToAdd,
    (emailAddress) => emailAddress,
  );

  const validationMessages = uniq(emailAddressesToValidate.flatMap((emailAddress) => {
    const result = emailAddressSchema.safeParse(emailAddress);
    if (!result.success) return result.error.issues.map((issue) => issue.message);

    if (
      judges.some((judge) => judge.emailAddress === emailAddress)
      || addedJudges.some((judge) => judge.emailAddress === emailAddress)
    ) {
      return [`${emailAddress} already added as judge.`];
    }

    return emailAddressCounts[emailAddress] > 1
      ? [`Duplicate email address: ${emailAddress}`]
      : [];
  }));

  const isValid = validationMessages.length === 0;

  const resetModalState = (): void => {
    setEmailsToAdd(undefined);
    setIsBeingValidated(false);
  };

  const handleAddButtonClick = (): void => {
    setIsBeingValidated(true);
    if (!isValid) return;

    onAddJudges(emailAddressesToAdd);
    resetModalState();
  };

  const handleDismissModal = (): void => {
    resetModalState();
    onDismissModal();
  };

  const handleSubmitModal = (): void => {
    resetModalState();
    onSubmitModal();
  };

  const renderJudges = (judges: AwardJudge[]): React.JSX.Element[] => {
    return (
      judges.map((judge, index) => {
        return (
          <FlexBox
            key={index}
            aligns='center'
            content='space-between'
          >
            <Text>{judge.user ? getUserNames([judge.user]) : judge.emailAddress}</Text>
            <FlexBox aligns='center'>
              <Switch
                aria-label='start judging'
                checked={judge.judgingStarted}
                disabled={!judge.judgingStarted}
                onChange={(_, data) => onUpdateJudgeStatus(judge, data.checked)}
              />
              {
                !judge.judgingStarted && !judge.hasJudged &&
                <Button
                  title='Delete'
                  appearance='transparent'
                  icon={<DismissRegular />}
                  onClick={() => onDeleteJudge(judge)}
                />
              }
            </FlexBox>
          </FlexBox>
        );
      })
    );
  }

  return (
    <Dialog
      open={isModalOpen}
      onOpenChange={(_, data) => {
        if (!data.open) handleDismissModal();
      }}
    >
      <DialogSurface className={mergeClasses(commonStyles.modalSurface, 'judge-assignment-modal')}>
        <DialogBody className={commonStyles.modalBody}>
          <DialogTitle
            action={(
              <Button
                appearance='transparent'
                title='Close'
                aria-label='Close'
                icon={<DismissRegular />}
                onClick={handleDismissModal}
              />
            )}
          >
            Edit Judges
          </DialogTitle>
          <DialogContent className={styles.modalContent}>
            <FormInput
              tooltipText={getTooltipContentByFieldName('edit-judges-modal', 'addNewText')}
              label='Edit judges'
              onChange={(value) => setEmailsToAdd(value)}
              validationMessage={isBeingValidated && validationMessages.length
                ? <ValidationMessageList messages={validationMessages} />
                : undefined}
              textAreaProps={{
                value: emailsToAdd || '',
              }}
            />
            <Button
              appearance='secondary'
              onClick={handleAddButtonClick}
            >
              Add
            </Button>
            <Text weight='semibold'>{`${activeJudgesCount} active / ${inactiveJudgesCount} inactive`}</Text>
            <FlexBox
              direction='column'
              className='modal-content__judges'
              isFullWidth
            >
              {renderJudges(judges)}
              {renderJudges(addedJudges)}
            </FlexBox>
          </DialogContent>
          <DialogActions>
            <Button
              appearance='secondary'
              onClick={handleDismissModal}
            >
              Cancel
            </Button>
            <Button
              appearance='primary'
              onClick={handleSubmitModal}
            >
              Save
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export const JudgeAssignmentModal = observer(JudgeAssignmentModalImpl);
